
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNotify } from '@/hooks/useNotify';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Tag, Wrench, FileSpreadsheet, Plus, Truck } from 'lucide-react';
import PageHeader from '@/components/layout/PageHeader';
import EntityActions from '@/components/shared/EntityActions';
import { useSupplierData } from '@/pages/suppliers/hooks/useSupplierData';
import { useSupplierFilterBuilder } from '@/pages/suppliers/hooks/useSupplierFilterBuilder';
import { FilterBuilder } from '@/components/invoices/FilterBuilder';
import FilterToolbar from '@/components/shared/FilterToolbar';
import { SUPPLIER_SORT_OPTIONS, type Supplier } from '@/pages/suppliers/types';

interface Category {
  id: string;
  name: string;
}

interface Trade {
  id: string;
  name: string;
}

// Extended supplier interface for rendering with categories and trades
interface SupplierWithDetails extends Supplier {
  categories: Category[];
  trades: Trade[];
}

const CostCenterSuppliersPage = () => {
  const { user, profile } = useAuth();
  const notify = useNotify();
  const navigate = useNavigate();
  const [suppliersWithDetails, setSuppliersWithDetails] = useState<SupplierWithDetails[]>([]);
  

  const {
    suppliers: searchedSuppliers,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
    loading,
  } = useSupplierData();

  const {
    draftGroups,
    appliedGroups,
    filteredSuppliers,
    hasActiveFilters,
    hasUnappliedChanges,
    canApplyFilters,
    addGroup,
    updateGroup,
    removeGroup,
    addRuleToGroup,
    updateRuleInGroup,
    removeRuleFromGroup,
    clearAllRules,
    applyFilters,
    removeAppliedRule,
    removeAppliedGroup,
    filterFields,
    filterEntities,
    filterAttributesByEntity
  } = useSupplierFilterBuilder(searchedSuppliers);

  // Transform suppliers to include category and trade details when data changes
  useEffect(() => {
    const fetchCategoriesAndTrades = async () => {
      if (!user || !profile?.company_id || searchedSuppliers.length === 0) {
        setSuppliersWithDetails([]);
        return;
      }

      try {
        // Fetch all categories and trades for this company
        const { data: categoriesData } = await supabase
          .rpc('get_all_categories', { user_company_id: profile?.company_id });
        
        const { data: tradesData } = await supabase
          .rpc('get_all_trades', { user_company_id: profile?.company_id });

        const categoriesMap = new Map(categoriesData?.map(cat => [cat.id, cat]) || []);
        const tradesMap = new Map(tradesData?.map(trade => [trade.id, trade]) || []);

        // Map suppliers with their categories and trades
        const suppliersWithDetails: SupplierWithDetails[] = searchedSuppliers.map(supplier => {
          // Get category and trade details from the enriched supplier data
          const categories = supplier.categoryName ? [{ id: '', name: supplier.categoryName }] : [];
          const trades = supplier.tradeNames?.map(name => ({ id: '', name })) || [];

          return {
            ...supplier,
            categories,
            trades
          };
        });

        setSuppliersWithDetails(suppliersWithDetails);
      } catch (error) {
        console.error('Error fetching categories and trades:', error);
        // Fallback: use basic supplier data
        const basicDetails: SupplierWithDetails[] = searchedSuppliers.map(supplier => ({
          ...supplier,
          categories: [],
          trades: []
        }));
        setSuppliersWithDetails(basicDetails);
      }
    };

    fetchCategoriesAndTrades();
  }, [searchedSuppliers, user, profile]);

  const deleteSupplier = async (id: string) => {
    try {
      const { error } = await supabase
        .from('suppliers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setSuppliersWithDetails(prev => prev.filter(s => s.id !== id));
      notify.success("Supplier deleted successfully");
    } catch (error) {
      console.error('Error deleting supplier:', error);
      notify.error(error);
    } finally {
      
    }
  };

  // Create filtered version of suppliersWithDetails
  const filteredSuppliersWithDetails = suppliersWithDetails.filter(supplier => 
    filteredSuppliers.some(filtered => filtered.id === supplier.id)
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Suppliers"
        description="Manage your suppliers and their information"
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            All Suppliers
          </CardTitle>

          <FilterToolbar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search suppliers..."
            sortOptions={SUPPLIER_SORT_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
            sortValue={sortBy}
            onSortChange={setSortBy}
            sortDir={sortDir}
            onSortDirChange={setSortDir}
            actions={
              <Button onClick={() => navigate('/cost-center/suppliers/create')} size="sm" className="px-4">
                <Plus className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Add Supplier</span>
                <span className="sm:hidden">Add</span>
              </Button>
            }
            advancedFilter={
              <FilterBuilder
                draftGroups={draftGroups}
                appliedGroups={appliedGroups}
                fields={filterFields}
                entities={filterEntities}
                attributesByEntity={filterAttributesByEntity}
                onAddGroup={addGroup}
                onUpdateGroup={updateGroup}
                onRemoveGroup={removeGroup}
                onAddRuleToGroup={addRuleToGroup}
                onUpdateRuleInGroup={updateRuleInGroup}
                onRemoveRuleFromGroup={removeRuleFromGroup}
                onClearAll={clearAllRules}
                onApplyFilters={applyFilters}
                hasActiveFilters={hasActiveFilters}
                hasUnappliedChanges={hasUnappliedChanges}
                canApplyFilters={canApplyFilters}
              />
            }
            hasAdvancedFilters={hasActiveFilters}
            advancedFilterCount={appliedGroups.reduce((c, g) => c + g.rules.filter(r => r.entity && r.attribute && r.value).length, 0)}
            onClearAdvancedFilters={clearAllRules}
          />
        </CardHeader>
        <CardContent>
        {loading ? (
          <div className="py-10 text-center">Loading suppliers...</div>
        ) : filteredSuppliersWithDetails.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">
            <p>{hasActiveFilters ? "No suppliers match the current filters." : "No suppliers found. Click the \"Add Supplier\" button to create one."}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="hidden sm:table-cell">Contact</TableHead>
                <TableHead className="hidden md:table-cell w-[12rem] whitespace-nowrap">Classifications</TableHead>
                <TableHead className="hidden md:table-cell w-[14rem]">Trades</TableHead>
                <TableHead className="text-right w-10">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSuppliersWithDetails.map((supplier) => (
                <TableRow
                  key={supplier.id}
                  className="hover:bg-muted/50 cursor-pointer"
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('button, a')) return;
                    navigate(`/cost-center/suppliers/${supplier.id}`);
                  }}
                >
                  <TableCell className="font-medium min-w-0 max-w-0 sm:max-w-none">
                    <span className="text-left truncate block w-full">
                      {supplier.name}
                    </span>
                    {supplier.website && (
                      <a
                        href={supplier.website.startsWith('http') ? supplier.website : `https://${supplier.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hidden sm:block text-xs text-muted-foreground mt-0.5 truncate hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {supplier.website}
                      </a>
                    )}
                    {/* Mobile subtext: contact + categories */}
                    <div className="sm:hidden text-xs text-muted-foreground mt-0.5 space-y-0.5">
                      {supplier.contact_name && <div className="truncate">{supplier.contact_name}</div>}
                      {supplier.email && <div className="truncate">{supplier.email}</div>}
                      {supplier.categories.length > 0 && (
                        <div className="truncate">{supplier.categories.map(c => c.name).join(', ')}</div>
                      )}
                      {supplier.trades.length > 0 && (
                        <div className="truncate">{supplier.trades.map(t => t.name).join(', ')}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <div className="space-y-1">
                      {supplier.contact_name && <div>{supplier.contact_name}</div>}
                      {supplier.email && <div className="text-sm text-muted-foreground">{supplier.email}</div>}
                      {supplier.phone && <div className="text-sm text-muted-foreground">{supplier.phone}</div>}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell w-[12rem] align-middle">
                    <div className="flex flex-wrap gap-1">
                      {supplier.categories.length > 0 ? (
                        supplier.categories.map((category) => (
                          <Badge key={category.id} variant="outline" className="whitespace-nowrap">
                            {category.name}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground text-sm">None</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell w-[14rem] align-middle">
                    {supplier.trades.length > 0 ? (
                      <div className="flex items-center gap-1 min-w-0">
                        <Badge
                          variant="outline"
                          className="flex items-center gap-1 min-w-0 max-w-full"
                          title={supplier.trades.map(t => t.name).join(', ')}
                        >
                          <Wrench className="h-3 w-3 shrink-0" />
                          <span className="truncate">{supplier.trades[0].name}</span>
                        </Badge>
                        {supplier.trades.length > 1 && (
                          <Badge variant="secondary" className="shrink-0 whitespace-nowrap">
                            +{supplier.trades.length - 1}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">None</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right w-10">
                    <EntityActions
                      size="sm"
                      actions={[
                        {
                          label: "View Line Items",
                          icon: FileSpreadsheet,
                          onClick: () => navigate(`/cost-center/suppliers/${supplier.id}/line-items`),
                        },
                        {
                          label: "Edit",
                          icon: Edit,
                          onClick: () => navigate(`/cost-center/suppliers/edit/${supplier.id}`),
                        },
                        {
                          label: "Delete",
                          icon: Trash2,
                          onClick: () => deleteSupplier(supplier.id),
                          destructive: true,
                          requiresConfirmation: {
                            title: "Delete Supplier",
                            description: `Are you sure you want to delete "${supplier.name}"? This action cannot be undone.`,
                          },
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CostCenterSuppliersPage;
